/**
 * 散点图组件
 * 专用于AI自信度与表现校准分析
 */

import React from 'react';
import { ScatterChart, Scatter, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, ReferenceLine } from 'recharts';
import { clsx } from 'clsx';

interface ScatterPlotProps {
  title: string;
  data: Array<{
    confidenceScore: number;
    actualAccuracy: number;
    sampleCount: number;
  }>;
  height?: number;
  showGrid?: boolean;
  showTooltip?: boolean;
  showCalibrationLine?: boolean;
  className?: string;
}

const ScatterPlot: React.FC<ScatterPlotProps> = ({
  title,
  data,
  height = 400,
  showGrid = true,
  showTooltip = true,
  showCalibrationLine = true,
  className
}) => {
  // 处理数据，根据样本数量设置点的大小
  const chartData = data.map(point => ({
    x: point.confidenceScore,
    y: point.actualAccuracy,
    z: Math.min(Math.max(point.sampleCount / 10, 3), 20), // 点大小范围 3-20
    sampleCount: point.sampleCount,
    confidenceScore: point.confidenceScore,
    actualAccuracy: point.actualAccuracy
  }));

  // 计算校准质量
  const calculateCalibrationQuality = () => {
    if (data.length === 0) return 0;
    
    let totalError = 0;
    let totalWeight = 0;
    
    data.forEach(point => {
      const error = Math.abs(point.confidenceScore - point.actualAccuracy);
      totalError += error * point.sampleCount;
      totalWeight += point.sampleCount;
    });
    
    const avgError = totalWeight > 0 ? totalError / totalWeight : 0;
    return Math.max(0, 1 - avgError); // 1 表示完美校准
  };

  const calibrationQuality = calculateCalibrationQuality();

  // 自定义Tooltip
  const CustomTooltip = ({ active, payload }: any) => {
    if (active && payload && payload.length) {
      const data = payload[0].payload;
      return (
        <div className="bg-white p-3 border rounded-lg shadow-lg">
          <div className="space-y-1 text-sm">
            <div>
              <span className="text-gray-600">AI自信度: </span>
              <span className="font-medium">{(data.confidenceScore * 100).toFixed(1)}%</span>
            </div>
            <div>
              <span className="text-gray-600">实际准确率: </span>
              <span className="font-medium">{(data.actualAccuracy * 100).toFixed(1)}%</span>
            </div>
            <div>
              <span className="text-gray-600">样本数量: </span>
              <span className="font-medium">{data.sampleCount.toLocaleString()}</span>
            </div>
            <div>
              <span className="text-gray-600">校准误差: </span>
              <span className={clsx(
                'font-medium',
                Math.abs(data.confidenceScore - data.actualAccuracy) > 0.1 ? 'text-red-600' : 'text-green-600'
              )}>
                {(Math.abs(data.confidenceScore - data.actualAccuracy) * 100).toFixed(1)}%
              </span>
            </div>
          </div>
        </div>
      );
    }
    return null;
  };

  // 获取校准质量等级
  const getCalibrationLevel = () => {
    if (calibrationQuality >= 0.9) return { level: 'excellent', color: 'text-green-600', label: '优秀' };
    if (calibrationQuality >= 0.8) return { level: 'good', color: 'text-blue-600', label: '良好' };
    if (calibrationQuality >= 0.7) return { level: 'fair', color: 'text-yellow-600', label: '一般' };
    return { level: 'poor', color: 'text-red-600', label: '需要改进' };
  };

  const calibrationLevel = getCalibrationLevel();

  return (
    <div className={clsx('card p-6', className)}>
      {/* 标题和校准质量指标 */}
      <div className="mb-6">
        <div className="flex items-center justify-between">
          <h3 className="text-lg font-medium text-gray-900">{title}</h3>
          <div className="text-right">
            <div className="text-sm text-gray-600">校准质量</div>
            <div className={clsx('text-lg font-semibold', calibrationLevel.color)}>
              {(calibrationQuality * 100).toFixed(1)}%
            </div>
            <div className={clsx('text-xs', calibrationLevel.color)}>
              {calibrationLevel.label}
            </div>
          </div>
        </div>
        
        <p className="text-sm text-gray-600 mt-1">
          散点越接近对角线，表示AI自信度与实际性能越一致
        </p>
      </div>

      {/* 散点图 */}
      <div className="w-full">
        <ResponsiveContainer width="100%" height={height}>
          <ScatterChart
            margin={{
              top: 20,
              right: 20,
              bottom: 20,
              left: 20
            }}
          >
            {showGrid && <CartesianGrid strokeDasharray="3 3" stroke="#f3f4f6" />}
            
            <XAxis
              type="number"
              dataKey="x"
              domain={[0, 1]}
              tickFormatter={(value) => `${(value * 100).toFixed(0)}%`}
              tick={{ fontSize: 12 }}
              axisLine={{ stroke: '#e5e7eb' }}
              tickLine={{ stroke: '#e5e7eb' }}
              label={{ value: 'AI自信度', position: 'insideBottom', offset: -10 }}
            />
            
            <YAxis
              type="number"
              dataKey="y"
              domain={[0, 1]}
              tickFormatter={(value) => `${(value * 100).toFixed(0)}%`}
              tick={{ fontSize: 12 }}
              axisLine={{ stroke: '#e5e7eb' }}
              tickLine={{ stroke: '#e5e7eb' }}
              label={{ value: '实际准确率', angle: -90, position: 'insideLeft' }}
            />
            
            {showTooltip && <Tooltip content={<CustomTooltip />} />}
            
            {/* 理想校准线 (y = x) */}
            {showCalibrationLine && (
              <ReferenceLine
                segment={[{ x: 0, y: 0 }, { x: 1, y: 1 }]}
                stroke="#6b7280"
                strokeDasharray="5 5"
                strokeWidth={2}
              />
            )}
            
            {/* 校准容忍带 */}
            <ReferenceLine
              segment={[{ x: 0, y: 0.1 }, { x: 0.9, y: 1 }]}
              stroke="#fbbf24"
              strokeDasharray="2 2"
              strokeWidth={1}
              opacity={0.5}
            />
            <ReferenceLine
              segment={[{ x: 0.1, y: 0 }, { x: 1, y: 0.9 }]}
              stroke="#fbbf24"
              strokeDasharray="2 2"
              strokeWidth={1}
              opacity={0.5}
            />
            
            {/* 数据点 */}
            <Scatter
              data={chartData}
              fill="#3b82f6"
              fillOpacity={0.6}
              stroke="#1d4ed8"
              strokeWidth={1}
            />
          </ScatterChart>
        </ResponsiveContainer>
      </div>

      {/* 分析说明 */}
      <div className="mt-4 pt-4 border-t border-gray-200">
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 text-sm">
          <div>
            <div className="text-gray-600">数据点数</div>
            <div className="font-medium">{data.length}</div>
          </div>
          <div>
            <div className="text-gray-600">总样本数</div>
            <div className="font-medium">
              {data.reduce((sum, point) => sum + point.sampleCount, 0).toLocaleString()}
            </div>
          </div>
          <div>
            <div className="text-gray-600">校准建议</div>
            <div className={clsx('font-medium', calibrationLevel.color)}>
              {calibrationQuality >= 0.8 ? '校准良好' : 
               calibrationQuality >= 0.7 ? '需要微调' : '需要重新校准'}
            </div>
          </div>
        </div>

        <div className="mt-3 text-xs text-gray-500">
          <div>• 点的大小表示样本数量</div>
          <div>• 虚线表示理想校准线 (AI自信度 = 实际准确率)</div>
          <div>• 黄色虚线表示可接受的校准误差范围 (±10%)</div>
        </div>
      </div>
    </div>
  );
};

export default ScatterPlot;